import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { css } from '@emotion/react';
import {
  addYears,
  eachMonthOfInterval,
  eachYearOfInterval,
  endOfDecade,
  endOfMonth,
  endOfYear,
  format,
  getMonth,
  getYear,
  isAfter,
  isBefore,
  isSameMonth,
  isSameYear,
  isWithinInterval,
  Locale,
  startOfMonth,
  startOfYear,
  subYears,
} from 'date-fns';
import range from 'lodash-es/range';
import { year_month_select_panel_css } from '../styles';
import { Stack } from '../../stack';
import { YearMonthCaption } from '../YearMonthCaption';
import { useDatePickerAttributes } from '../hooks/useDatePickerAttributes';
import { buildCSSWithLength } from '../../../styles';
import { DisplayMode } from '../types';

export type CalendarType = 'day' | 'month' | 'year' | 'year_range';

export interface YearMonthCalendarProps {
  className?: string;
  type: CalendarType;
  locale: Locale;
  currentDate: Date;
  displayMode?: DisplayMode;
  disabled?: boolean;
  disabledDates?: (value: Date) => boolean;
  selectedRange?: {
    from: Date | undefined;
    to?: Date | undefined;
  };
  onChange?: (value: Date | null) => void;
  onClose?: () => void;
}

export const YearMonthCalendar = ({
  className,
  type = 'month',
  locale,
  disabledDates,
  displayMode = 'popover',
  currentDate,
  selectedRange,
  onChange,
  onClose,
}: YearMonthCalendarProps) => {
  const { panelSize } = useDatePickerAttributes(displayMode);
  const [currentValue, setCurrentValue] = useState<Date>(currentDate || new Date());
  const [panelType, setPanelType] = useState<CalendarType>(type);
  const panelButtonStyles = [
    year_month_select_panel_css,
    css`
      ${buildCSSWithLength('width', panelSize.width)};
      ${buildCSSWithLength('height', panelSize.height)};
    `,
  ];

  const monthList = useMemo(() => {
    return eachMonthOfInterval({
      start: startOfYear(currentValue),
      end: endOfYear(currentValue),
    });
  }, [currentValue]);

  const yearList = useMemo(() => {
    return eachYearOfInterval({
      start: subYears(endOfDecade(currentValue), 10),
      end: addYears(endOfDecade(currentValue), 1),
    });
  }, [currentValue]);

  const yearRangeList = useMemo(() => {
    const datum_year = Math.floor(getYear(endOfDecade(currentValue)) / 100) * 100;
    return range(datum_year - 10, datum_year + 110, 10).map((start) => ({
      start: new Date(String(start)),
      end: addYears(new Date(String(start)), 9),
    }));
  }, [currentValue]);

  const handleClickPanel = (date: Date) => {
    onChange?.(date);
    onClose?.();
  };

  const renderMonthPanel = () => {
    return monthList.map((month: Date, i) => {
      const start = selectedRange?.from ? isSameMonth(selectedRange.from, month) : null;
      const end = selectedRange?.to ? isSameMonth(selectedRange.to, month) : null;
      const single = (start && end) || (!selectedRange?.to && start) || (!selectedRange?.from && end);
      const middle =
        selectedRange?.from && selectedRange.to
          ? isAfter(month, endOfMonth(selectedRange.from)) && isBefore(month, startOfMonth(selectedRange.to))
          : false;
      const disabled = disabledDates?.(month) && !start && !end;
      return (
        <button
          type='button'
          disabled={disabled}
          className={classNames('pds-year-month-panel', {
            disabled: disabledDates?.(month),
            start,
            end,
            middle,
            single,
          })}
          key={i}
          css={[
            panelButtonStyles,
            (start || end) &&
              css`
                ${buildCSSWithLength('width', panelSize.width - 2)};
              `,
          ]}
          data-testid={`pds-date-picker-month-button-${month.getMonth() + 1}`}
          onClick={() => handleClickPanel(month)}
        >
          {locale?.code === 'ko' ? format(month, 'PPP', { locale }).slice(6, 9) : format(month, 'MMM', { locale })}
        </button>
      );
    });
  };
  const renderYearPanel = () => {
    return yearList.map((year: Date, i) => {
      const start = selectedRange?.from ? isSameYear(selectedRange.from, year) : null;
      const end = selectedRange?.to ? isSameYear(selectedRange.to, year) : null;
      const middle =
        selectedRange?.from && selectedRange.to
          ? isAfter(year, endOfYear(selectedRange.from)) && isBefore(year, startOfYear(selectedRange.to))
          : false;
      const single = (start && end) || (!selectedRange?.to && start) || (!selectedRange?.from && end);
      const disabledYearStartAndEnd = disabledDates?.(startOfYear(year)) && disabledDates?.(endOfYear(year));
      const disabled = disabledYearStartAndEnd && !start && !end;
      return (
        <button
          type='button'
          disabled={disabled}
          className={classNames('pds-year-month-panel', { disabled, start, end, middle, single })}
          key={i}
          css={[
            panelButtonStyles,
            (start || end) &&
              css`
                ${buildCSSWithLength('width', panelSize.width - 2)};
              `,
          ]}
          onClick={() => {
            year.setMonth(getMonth(currentValue));
            if (type === 'month') {
              setCurrentValue(year);
              setPanelType('month');
              return;
            }
            handleClickPanel(year);
          }}
        >
          {format(year, 'yyyy')}
        </button>
      );
    });
  };

  const renderYearRangePanel = () => {
    return yearRangeList.map((year_range, i) => {
      const start = selectedRange?.from ? isWithinInterval(selectedRange.from, year_range) : null;
      const end = selectedRange?.to ? isWithinInterval(selectedRange.to, year_range) : null;
      const single = (start && end) || (!selectedRange?.to && start) || (!selectedRange?.from && end);
      const middle =
        selectedRange?.from && selectedRange.to
          ? isAfter(year_range.start, endOfYear(selectedRange.from)) &&
            isBefore(year_range.end, startOfYear(selectedRange.to))
          : false;
      const disabled = disabledDates?.(year_range.start) && disabledDates?.(year_range.end) && !start && !end;
      return (
        <button
          type='button'
          disabled={disabled}
          className={classNames('pds-year-month-panel', { start, end, middle, single })}
          key={i}
          css={[
            panelButtonStyles,
            (start || end) &&
              css`
                ${buildCSSWithLength('width', panelSize.width - 2)};
              `,
          ]}
          onClick={() => {
            year_range.start.setMonth(getMonth(currentValue));
            setCurrentValue(year_range.start);
            setPanelType('year');
          }}
        >
          {format(year_range.start, 'yyyy')}~{format(year_range.end, 'yyyy')}
        </button>
      );
    });
  };

  const caption_props = useMemo(() => {
    switch (panelType) {
      case 'month':
        return {
          label:
            locale?.code === 'ko'
              ? format(currentValue, 'PPP', { locale }).slice(0, 5)
              : format(currentValue, 'yyyy', { locale }),
          onPrev: () => setCurrentValue((date) => subYears(date, 1)),
          onNext: () => setCurrentValue((date) => addYears(date, 1)),
          onClickLabel: () => setPanelType('year'),
        };
      case 'year':
        return {
          label: `${format(yearList[0], 'yyyy')}~${format(yearList[11], 'yyyy')}`,
          onPrev: () => setCurrentValue((date) => subYears(date, 10)),
          onNext: () => setCurrentValue((date) => addYears(date, 10)),
          onClickLabel: () => setPanelType('year_range'),
        };
      case 'year_range':
        return {
          label: `${format(yearRangeList[0].start, 'yyyy')}~${format(yearRangeList[11].end, 'yyyy')}`,
          onPrev: () => setCurrentValue((year) => subYears(year, 120)),
          onNext: () => setCurrentValue((year) => addYears(year, 120)),
          disabledLabel: true,
        };
      default:
        return {
          label: '',
        };
    }
  }, [panelType, currentValue]);

  const renderPanel = () => {
    switch (panelType) {
      case 'month':
        return renderMonthPanel();
      case 'year':
        return renderYearPanel();
      case 'year_range':
        return renderYearRangePanel();
      default:
        return <></>;
    }
  };

  if (type === 'day') {
    return <></>;
  }

  return (
    <Stack
      direction='column'
      gap={16}
      className={classNames('pds-month-picker', className)}
      css={[
        css`
          position: relative;
          ${buildCSSWithLength('width', panelSize.width * 3)}
        `,
      ]}
    >
      <YearMonthCaption
        displayMode={displayMode}
        {...caption_props}
        disabledPrev={disabledDates?.(startOfYear(currentValue))}
        disabledNext={disabledDates?.(endOfYear(currentValue))}
      />
      <Stack direction='row' style={{ flexWrap: 'wrap' }}>
        {renderPanel()}
      </Stack>
    </Stack>
  );
};
